// ** UI
import TextField, { type TextFieldProps } from '@mui/material/TextField';

// ** Types
import type { FormikProps } from 'formik';
import type { FormHelperTextProps } from '@mui/material/FormHelperText';

type FormikTextProps<T> = {
	formikProps: FormikProps<T>;
	noUnderline?: boolean;
	uncontrolled?: boolean;
	suffix?: string;
	helperTextProps?: FormHelperTextProps;
} & TextFieldProps;

const FormikTextField = <T,>({
	formikProps,
	name,
	placeholder,
	disabled,
	prefix,
	suffix,
	noUnderline,
	helperTextProps,
	uncontrolled,
	defaultValue,
	sx,
	...other
}: FormikTextProps<T>) => {
	const { value, error, touched } = formikProps.getFieldMeta(name || '');

	// If uncontrolled, don't bind the value from Formik, use defaultValue or other prop
	const inputValue = uncontrolled ? defaultValue : value;

	return (
		<TextField
			variant="standard"
			margin="normal"
			onChange={formikProps.handleChange}
			onBlur={formikProps.handleBlur}
			// Either controlled value from Formik or uncontrolled value
			value={inputValue}
			error={Boolean(error && touched)}
			helperText={touched && error ? error : other.helperText}
			placeholder={disabled ? undefined : placeholder}
			disabled={disabled}
			name={name}
			slotProps={{
				input: { disableUnderline: noUnderline, readOnly: uncontrolled },
				htmlInput: { prefix, suffix },
				formHelperText: helperTextProps
			}}
			sx={{
				...sx,
				...(uncontrolled && {
					'& .MuiInputBase-root': {
						'&:before': {
							borderBottom: 'none'
						},
						'&:after': {
							borderBottom: 'none'
						},
						'&:hover:before': {
							borderBottom: 'none'
						}
					},
					'& .MuiAutocomplete-inputRoot': {
						'& .MuiOutlinedInput-notchedOutline': {
							border: 'none'
						},
						'&:hover .MuiOutlinedInput-notchedOutline': {
							border: 'none'
						},
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
							border: 'none'
						}
					}
				})
			}}
			{...other}
		/>
	);
};

export default FormikTextField;
